import { Controller } from "stimulus";

// Home "Live now" / premiere banner: live counts UP from go-live; premiere counts DOWN to the scheduled
// start, flips to LIVE, then to a muted "ending soon" state during the afterparty (all in place, no reload).
export default class BannerComponent extends Controller {
  static targets = ["timer", "timerOverdue", "waitingPill", "livePill", "endingSoonPill", "ctaLabel"];
  static values = {
    timerMs: Number,
    playbackEndsAtMs: Number,
    variant: String,
    started: Boolean,
    viewersUrl: String,
    pollMs: { type: Number, default: 3000 },
  };

  connect() {
    if (!this.startedValue) {
      this.premiereTick();
      this.timerInterval = window.setInterval(() => this.premiereTick(), 1000);
    } else if (this.timerMsValue > 0) {
      this.renderTimer();
      this.timerInterval = window.setInterval(() => this.renderTimer(), 1000);
    }

    this.pollViewers();
    this.startPolling();

    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.stopPolling();
      } else {
        this.pollViewers();
        this.startPolling();
      }
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  disconnect() {
    if (this.timerInterval) window.clearInterval(this.timerInterval);
    this.stopPolling();
    if (this.onVisibilityChange) document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  startPolling() {
    this.stopPolling();
    this.pollInterval = window.setInterval(() => this.pollViewers(), this.pollMsValue);
  }

  stopPolling() {
    if (this.pollInterval) {
      window.clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  // Phase machine off the raw client clock (cosmetic), for any not-yet-started event of either
  // kind: waiting countdown -> LIVE -> ending soon (premiere), or waiting countdown -> LIVE /
  // static overdue state (stream, webhook-driven - see the comment below).
  premiereTick() {
    const now = Date.now();

    if (this.playbackEndsAtMsValue > 0 && now >= this.playbackEndsAtMsValue) {
      this.goAfterparty();
      return;
    }

    if (now >= this.timerMsValue) {
      if (this.variantValue === "premiere") {
        // Interval deliberately kept running: goLive() doesn't start a replacement one, and a
        // later tick still needs to catch playbackEndsAtMsValue above to reach goAfterparty().
        this.goLive();
        return;
      }

      // Webhook-driven - reaching starts_at doesn't mean the creator has actually gone live.
      // Stop ticking and show a static state; unlike the live page there's no turbo_stream
      // subscription on the home page to push a later update, so this settles for real on the
      // next natural page load once mark_live! has actually landed.
      if (this.timerInterval) window.clearInterval(this.timerInterval);
      if (this.hasTimerTarget) this.timerTarget.classList.add("hidden");
      if (this.hasTimerOverdueTarget) this.timerOverdueTarget.classList.remove("hidden");
      return;
    }

    if (this.hasTimerTarget) this.timerTarget.textContent = this.format(this.timerMsValue - now);
  }

  // live: count up from go-live time. playbackEndsAtMsValue is only ever non-zero for a
  // premiere, so this guard is a no-op for a stream - it exists so a premiere that was
  // already playing at page load (started-value true, so premiereTick never runs) still
  // flips to the afterparty state instead of counting up forever.
  renderTimer() {
    if (this.playbackEndsAtMsValue > 0 && Date.now() >= this.playbackEndsAtMsValue) {
      this.goAfterparty();
      return;
    }

    if (!this.hasTimerTarget) return;

    const elapsed = Math.max(0, Date.now() - this.timerMsValue);
    const totalSeconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(totalSeconds / 60); // not capped at 60: 63 min reads "63:xx"
    const seconds = totalSeconds % 60;
    this.timerTarget.textContent = `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  // Flip from the waiting/countdown state to the pulsing LIVE state in place (premiere only). Idempotent.
  goLive() {
    if (this.hasTimerTarget) this.timerTarget.textContent = "";
    if (this.hasWaitingPillTarget) this.waitingPillTarget.classList.add("hidden");
    if (this.hasLivePillTarget) this.livePillTarget.classList.remove("hidden");
    if (this.hasCtaLabelTarget) this.ctaLabelTarget.textContent = "Watch now";
  }

  // Episode finished playing; show the muted "ending soon" pill and stop the timer (terminal). Idempotent.
  goAfterparty() {
    if (this.timerInterval) {
      window.clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.hasTimerTarget) this.timerTarget.textContent = "";
    if (this.hasWaitingPillTarget) this.waitingPillTarget.classList.add("hidden");
    if (this.hasLivePillTarget) this.livePillTarget.classList.add("hidden");
    if (this.hasEndingSoonPillTarget) this.endingSoonPillTarget.classList.remove("hidden");
    if (this.hasCtaLabelTarget) this.ctaLabelTarget.textContent = "Join the chat";
  }

  pollViewers() {
    if (!this.viewersUrlValue) return;

    fetch(this.viewersUrlValue, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) return;
        // Set the rolling-number controller's value attr (not textContent) so it animates.
        const el = this.element.querySelector("[data-live-viewer-count]");
        if (el) el.setAttribute("data-rolling-number-count-value", data.count);
      })
      .catch(() => {});
  }

  // MM:SS, widening to H:MM:SS once an hour or more remains (premiere countdown).
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
